import { GetterTree } from "vuex";
import { RootState } from "../store";
import { MarkerFeature } from "../../../../utils/models/MarkerFeature";
import { plainToClass } from "class-transformer";
import { LineStringFeature } from "../../../../utils/models/LineStringFeature";
import { Route } from "../../../../utils/models/Route";
import {
  length as turfLength,
  distance as turfDistance,
  bbox as turfBbox,
} from "@turf/turf";
import { MeasurementPrefUtility } from "../../../../utils/helpers/MeasurementPref";
import { elevationGainFromCoordinates } from "../../../../utils/elevation/elevationGain";
import { normalizeNotificationDistance } from "../../utils/normalizeNotificationDistance";
import {
  buildElevationChartData,
  equalDistanceSampleIndices,
} from "../../../../utils/elevation/buildElevationChartData";
import { surfaceKeyToInt } from "../../../../utils/elevation/surfaces";
import { HikeTimeUtility, PrettyTime } from "../../../../utils/helpers/HikeTime";
import { PlanTrailState } from "./PlanTrailModule";
import moment from 'moment';
import 'moment-timezone';
import {HiikerMapStyle, SavedCenter} from "../../../../utils/models/MapStyles";
import {RevenueCatWrapper} from "../../../../utils/RevenueCatWrapper";
import { detectStageConnectors, detectStageEndMarkers, findReconnectableStageLinks, StageConnector, StageEndMarker, ReconnectableStageLink } from "../../services/stage-connectors";
import { resolveAttachedDestinations, AttachedStageDestination, resolveAttachedTrailStart, AttachedTrailStart } from "../../services/stage-destinations";
import { getSunTimes } from "../../../../utils/helpers/SunTimes";
import { canDeriveMasterTrail } from "../../utils/masterTrail";
import { buildItinerary, ItineraryData } from "../../services/itinerary";

// Module-level cache for routesWithStats (outside Vuex state to avoid getter mutation anti-pattern)
let _routeStatsCache: { version: number; data: any[] } | null = null;

// How many days apart to space day-boundary markers on the combined overview
// elevation profile. Short plans show every day; long thru-hikes (PCT-scale,
// 100+ days) would otherwise overload the chart with badges, so the interval
// widens as day count grows: every day up to 20, every 2nd up to 50, every
// 3rd up to 100, then +1 to the interval per further 50 days.
function dayMarkerStep(totalDays: number): number {
  if (totalDays <= 20) return 1;
  if (totalDays <= 50) return 2;
  return 3 + Math.floor((totalDays - 51) / 50);
}

const getters: GetterTree<PlanTrailState, RootState> = {
  // Legacy computed properties for backward compatibility
  activeRoute(state): Route | null {
    return state.activeRouteKey && state.routes[state.activeRouteKey]
      ? state.routes[state.activeRouteKey]
      : null;
  },

  // Convert routes object to array for components
  routesArray(state): Route[] {
    return Object.keys(state.routes)
      .map(key => {
        const route = state.routes[key];
        return {
          id: route.id,
          name: route.name,
          position: route.position,
          markers: route.markers,
          lineStrings: route.lineStrings,
          created_at: route.created_at,
          updated_at: route.updated_at,
          route_style: route.route_style,
          day: route.day,
          undoMarkers: route.undoMarkers,
          redoMarkers: route.redoMarkers,
          undoLineStrings: route.undoLineStrings,
          redoLineStrings: route.redoLineStrings,
          // Include reference fields - always include plannedTrailId if present
          plannedTrailId: route.plannedTrailId,
          isReference: route.isReference,
          referencedHikes: route.referencedHikes
        };
      })
      .sort((a, b) => a.position - b.position);
  },

  activeRouteId(state): string | null {
    return state.activeRouteKey && state.routes[state.activeRouteKey]
      ? state.routes[state.activeRouteKey].id
      : null;
  },

  getLocalizedPlannedToHikeOn(state) {
    const localDateTime = moment.utc(state.plannedToHikeOn).local();
    const localizedDateTime = localDateTime.format('YYYY-MM-DDTHH:mm');
    return localizedDateTime;
  },
  getLastUndoMarkers(state, getters) {
    return state.undoMarkers[state.undoMarkers.length - 1]
  },
  getLastRedoMarkers(state, getters) {
    return state.redoMarkers[state.redoMarkers.length - 1]
  },
  getLastUndoLineStrings(state, getters) {
    return state.undoLineStrings[state.undoLineStrings.length - 1]
  },
  getLastRedoLineStrings(state, getters) {
    return state.redoLineStrings[state.redoLineStrings.length - 1]
  },
  markersFeatureCollection(state, getters) {
    const activeRoute = getters.activeRoute;
    if (!activeRoute) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    return {
      type: 'FeatureCollection',
      features: activeRoute.markers.map(marker => {
        // @ts-ignore
        const markerFeature: MarkerFeature = plainToClass(MarkerFeature, marker)
        return markerFeature.toGeoJson()
      })
    }
  },
  async initialMapSettings(state): Promise<mapboxgl.MapboxOptions> {
    const isPro = await RevenueCatWrapper.isProOrProPlus()
    const style = HiikerMapStyle.getCurrentStyle(isPro).styleUrl;
    const currentCenter = SavedCenter.getCurrentCenter()
    return {
      container: 'map',
      center: currentCenter.lngLat(),
      maxPitch: 85,
      zoom: currentCenter.zoom,
      style: style
    }
  },
  trailDistanceString(state, getters) {
    let distance = turfLength(getters.lineStringFeatureCollection, { units: 'meters' })
    return MeasurementPrefUtility.preference().smallUnitFormatter(distance, true)
  },
  elevationGainString(state, getters) {
    let elevationGainTotal = 0;

    const activeRoute = getters.activeRoute;
    if (!activeRoute) return '0m';

    activeRoute.lineStrings.forEach((lineString) => {
      const lineStringFeature = plainToClass(LineStringFeature, lineString);
      const coordinates = lineStringFeature.lineStringGeometry.coordinates; // Assuming this contains altitude data
      elevationGainTotal += elevationGainFromCoordinates(coordinates);
    });

    return MeasurementPrefUtility.preference().smallUnitFormatter(elevationGainTotal, false);
  },
  lineStringFeatureCollection(state, getters) {
    const activeRoute = getters.activeRoute;
    if (!activeRoute) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    return {
      type: 'FeatureCollection',
      features: activeRoute.lineStrings.map(lineString => {
        const lineStringFeature: LineStringFeature = plainToClass(LineStringFeature, lineString)
        return lineStringFeature.toGeoJson()
      })
    }
  },
  // Legacy getter - removed. Use activeRoute.markers instead.
  // Legacy getter - removed. Use activeRoute.lineStrings instead.
  // Build the elevation-profile data for the shared <ElevationProfile> component.
  //
  // Distance/sampling/gain-loss/units are all delegated to the shared
  // buildElevationChartData util (same equal-distance sampling every renderer
  // uses). Two planner-specific extras ride along:
  //  - `coordToLineString`: per-sampled-point map back to
  //    `{ routeKey, lineStringIndex, coordIndex }` in route geometry, which the
  //    hover-sync and click→insert-control-point flows need. It is aligned to
  //    the SAME sample indices the builder picks (both call
  //    equalDistanceSampleIndices with the same cumulative distances +
  //    maxPoints), so `coordToLineString[i]` corresponds to `points[i]`.
  //  - `dayMarkers`: in overview mode (no route selected, all routes charted
  //    combined), where each day starts along the distance axis.
  createChartData(state, getters) {
    const activeRoute = getters.activeRoute;
    // Overview mode: with no route selected, chart the whole trail's profile —
    // but only the LAST main ('route'-style) line of each day. Alt/spur/transport
    // lines are excluded, and where a day has several main routes only its final
    // one is plotted, so the profile follows the primary itinerary per day.
    const chartRoutes: Array<{ routeKey: string; route: Route }> = activeRoute
      ? [{ routeKey: state.activeRouteKey as string, route: activeRoute }]
      : (() => {
          const isMainRoute = (r: Route) => !r.route_style || r.route_style === 'route';
          // routesArray is sorted by position asc, so the last main route seen
          // for a day overwrites earlier ones — leaving the day's final route.
          const lastMainRouteByDay = new Map<number, Route>();
          getters.routesArray.forEach((route: Route) => {
            if (!isMainRoute(route)) return;
            lastMainRouteByDay.set(route.day ?? 1, route);
          });
          return [...lastMainRouteByDay.values()]
            .sort((a, b) => a.position - b.position)
            .map(route => ({ routeKey: route.id, route }));
        })();
    if (chartRoutes.length === 0) return null;

    const measurementUtils = MeasurementPrefUtility.preference();

    // Flatten every charted route's linestring coordinates into parallel
    // full-resolution arrays, carrying the per-coordinate surface integer and
    // the origin route/linestring/coordinate index for reverse mapping.
    const coordinates: number[][] = [];
    const surfacesFull: number[] = [];
    const coordToLineStringFull: { routeKey: string; lineStringIndex: number; coordIndex: number }[] = [];

    // Full-resolution cumulative distance (metres), parallel to `coordinates`.
    // Computed here rather than left to the shared builder because it must
    // RESTART at each route boundary: the combined overview profile sums route
    // lengths without a phantom straight-line hop between stages (stages can
    // be disconnected, e.g. a drive between trailheads). Segment distances use
    // the same turfDistance the builder uses, so passing this array back in
    // yields identical sample indices.
    const cumDist: number[] = [];
    let cumulativeDistance = 0;
    let prevCoord: number[] | null = null;

    // Overview mode: where each day begins along the combined distance axis,
    // so the chart can draw day-boundary indicators.
    const dayMarkers: Array<{ x: number; day: number; label: string }> = [];
    const seenDays = new Set<number>();

    for (const { routeKey, route } of chartRoutes) {
      // Distance restarts at each route boundary (see cumDist note above)
      prevCoord = null;

      if (!activeRoute) {
        const day = route.day ?? 1;
        if (!seenDays.has(day)) {
          seenDays.add(day);
          dayMarkers.push({
            x: measurementUtils.bigUnitValue(cumulativeDistance / 1000),
            day,
            // The bare day NUMBER, not the custom name — matches the same
            // numbering shown as the day badge in the left panel and on the
            // map's stage markers, and stays legible in the pill's limited
            // width regardless of how long a custom day name is.
            label: String(day),
          });
        }
      }

      for (let lsIndex = 0; lsIndex < route.lineStrings.length; lsIndex++) {
        const ls = route.lineStrings[lsIndex];
        const coords = ls.lineStringGeometry.coordinates;
        const surfaceValues = ls.lineStringProperties?.extras?.surface?.values;

        // Pre-index surface segments for this linestring (avoids linear scan per point)
        let surfaceIdx = 0;

        for (let cIndex = 0; cIndex < coords.length; cIndex++) {
          const coord = coords[cIndex];

          if (prevCoord) {
            cumulativeDistance += turfDistance(
              prevCoord.slice(0, 2),
              coord.slice(0, 2),
              { units: 'meters' }
            );
          }
          prevCoord = coord;
          cumDist.push(cumulativeDistance);

          coordinates.push(coord);
          coordToLineStringFull.push({ routeKey, lineStringIndex: lsIndex, coordIndex: cIndex });

          // Surface lookup — resolve to the canonical integer (wire value). Missing/
          // unrecognised surfaces resolve to 0 (unknown), matching the previous map.
          let surfaceInt = 0;
          if (surfaceValues) {
            while (surfaceIdx < surfaceValues.length) {
              const [startIdx, endIdx, sType] = surfaceValues[surfaceIdx];
              if (cIndex < endIdx) {
                if (cIndex >= startIdx) {
                  surfaceInt = typeof sType === 'number'
                    ? sType
                    : (surfaceKeyToInt[sType] ?? 0);
                }
                break;
              }
              surfaceIdx++;
            }
          }
          surfacesFull.push(surfaceInt);
        }
      }
    }

    if (coordinates.length === 0) return null;

    const maxPoints = 1000;

    const sampleIndices = equalDistanceSampleIndices(cumDist, maxPoints);
    const coordToLineString = sampleIndices.map(i => coordToLineStringFull[i]);

    const data = buildElevationChartData({
      coordinates,
      surfaces: surfacesFull,
      distances: cumDist,
      maxPoints,
      measurementPref: measurementUtils,
    });

    // Thin the markers for long trails so the chart doesn't get overloaded
    // with day badges (see dayMarkerStep) — keep every Nth day in charted
    // order, plus the last day so the hike's end is always visible.
    const step = dayMarkerStep(dayMarkers.length);
    const thinnedDayMarkers = step <= 1
      ? dayMarkers
      : dayMarkers.filter((_, idx) => idx % step === 0 || idx === dayMarkers.length - 1);

    // Spread the shared ElevationChartData and attach the planner-specific
    // extras. The shared <ElevationProfile> reads only the known
    // ElevationChartData fields (plus the optional dayMarkers prop passed
    // separately); coordToLineString is consumed by PlanTrailElevation for
    // hover/click resolution. A single-day plan doesn't need a "Day 1" flag
    // cluttering the chart, so dayMarkers only populate with 2+ days.
    return {
      ...data,
      coordToLineString,
      dayMarkers: seenDays.size >= 2 ? thinnedDayMarkers : [],
    };
  },
  // Legacy getter - removed. Use activeRoute.markers[activeRoute.markers.length - 1] instead.
  // Legacy getter - removed. Use activeRoute.markers.length === 0 instead.
  getPtv2ImageAttributes(state) {
    const activePhotoIds = state.photosIds.filter(
      (id: string) => !state.photosMarkedForRemoval.includes(id)
    );

    return state.photosIds.map((photoId: string) => {
      const attributes: any = {
        parentClientId: state.clientId,
        clientId: photoId,
        imageableType: 'Hike',
        isCover: activePhotoIds.indexOf(photoId) === 0,
      };

      if (state.photoExistingIds[photoId]) {
        attributes.id = state.photoExistingIds[photoId];
      }

      if (state.photoSignedIds[photoId]) {
        attributes.signedId = state.photoSignedIds[photoId];
      }

      if (state.photosMarkedForRemoval.includes(photoId)) {
        attributes._destroy = true;
      }

      return attributes;
    });
  },

  existingHikeImages(state) {
    return state.existingHikeImages;
  },

  // Route management getters (using new structure)

  routesWithStats(state, getters): Array<Route & {distance: number, ascent: number, duration: string, durationSeconds: number}> {
    // Return cached result if route geometry hasn't changed
    if (_routeStatsCache && _routeStatsCache.version === state._statsVersion) {
      return _routeStatsCache.data;
    }

    const result = getters.routesArray.map((route: Route) => {
      // Calculate distance
      const routeLineStrings = {
        type: 'FeatureCollection',
        features: route.lineStrings.map(lineString => {
          const lineStringFeature: LineStringFeature = plainToClass(LineStringFeature, lineString);
          return lineStringFeature.toGeoJson();
        })
      };
      const distance = turfLength(routeLineStrings, { units: 'meters' });

      // For transport routes, skip elevation gain and duration calculations
      if (route.route_style === 'transport') {
        return {
          ...route,
          distance,
          ascent: 0,
          duration: '',
          durationSeconds: 0
        };
      }

      // Calculate elevation gain for non-transport routes
      let elevationGain = 0;
      route.lineStrings.forEach((lineString) => {
        const lineStringFeature = plainToClass(LineStringFeature, lineString);
        const coordinates = lineStringFeature.lineStringGeometry.coordinates;
        elevationGain += elevationGainFromCoordinates(coordinates);
      });

      // Calculate duration using HikeTimeUtility
      const durationSeconds = HikeTimeUtility.preference().secondsDuration(elevationGain, distance);
      const duration = new PrettyTime(durationSeconds).prettyTime();

      return {
        ...route,
        distance,
        ascent: elevationGain,
        duration,
        durationSeconds,
        day: route.day
      };
    });

    // Cache the result (module-level, not in Vuex state)
    _routeStatsCache = { version: state._statsVersion, data: result };
    return result;
  },

  totalDistance(state, getters): number {
    return getters.routesWithStats.reduce((total: number, route: any) => total + route.distance, 0);
  },

  totalDistanceExcludingTransport(state, getters): number {
    return getters.routesWithStats.reduce((total: number, route: any) => {
      // Only add distance if it's a main route (exclude transport, alt, and spur)
      if (route.route_style !== 'transport' && route.route_style !== 'alt' && route.route_style !== 'spur') {
        return total + route.distance;
      }
      return total;
    }, 0);
  },

  hasTransportRoutes(state, getters): boolean {
    return getters.routesArray.some((route: Route) => route.route_style === 'transport');
  },

  isTransportOnlyPlan(state, getters): boolean {
    const routes: Route[] = getters.routesArray;
    return routes.length > 0 && routes.every((route: Route) => route.route_style === 'transport');
  },

  // Check if the plan has at least one main route (route style = 'route')
  hasMainRoute(state, getters): boolean {
    const routes: Route[] = getters.routesArray;
    // Default route_style to 'route' if undefined for backwards compatibility
    return routes.some((route: Route) => (route.route_style || 'route') === 'route');
  },

  // Validate that each hike day has at least one main route
  validateHikeDaysHaveMainRoute(state, getters): { valid: boolean; invalidDays: number[] } {
    const routesByDay = getters.routesByDay;
    const invalidDays: number[] = [];

    // Check each day
    Object.keys(routesByDay).forEach(dayKey => {
      const dayNumber = parseInt(dayKey);
      const dayRoutes = routesByDay[dayNumber];

      // Check if this day has at least one 'route' or 'transport' style route
      // Default route_style to 'route' if undefined for backwards compatibility
      const hasMainRoute = dayRoutes.some((route: any) => {
        const style = route.route_style || 'route';
        return style === 'route' || style === 'transport';
      });

      if (!hasMainRoute) {
        invalidDays.push(dayNumber);
      }
    });

    return {
      valid: invalidDays.length === 0,
      invalidDays
    };
  },

  totalAscent(state, getters): number {
    return getters.routesWithStats.reduce((total: number, route: any) => {
      // Only add ascent if it's a main route (exclude transport, alt, and spur)
      if (route.route_style !== 'transport' && route.route_style !== 'alt' && route.route_style !== 'spur') {
        return total + route.ascent;
      }
      return total;
    }, 0);
  },

  totalElevationLoss(state, getters): number {
    let totalLoss = 0;

    getters.routesArray.forEach((route: Route) => {
      // Skip transport routes for elevation calculations
      if (route.route_style === 'transport') return;

      route.lineStrings.forEach((lineString) => {
        const lineStringFeature = plainToClass(LineStringFeature, lineString);
        const coordinates = lineStringFeature.lineStringGeometry.coordinates;

        if (coordinates.length < 2) return;

        for (let i = 1; i < coordinates.length; i++) {
          const elevationChange = coordinates[i][2] - coordinates[i - 1][2];

          // Count negative elevation changes as loss
          if (elevationChange < -6) { // Using same 6m threshold as gain
            totalLoss += Math.abs(elevationChange);
          }
        }
      });
    });

    return totalLoss;
  },

  // Total estimated hike time across all main routes (excludes transport, alt,
  // and spur to match totalDistance/totalAscent). Sum the raw seconds and format
  // once so minutes aren't lost the way per-route string parsing would.
  totalDurationSeconds(state, getters): number {
    return getters.routesWithStats.reduce((total: number, route: any) => {
      if (route.route_style !== 'transport' && route.route_style !== 'alt' && route.route_style !== 'spur') {
        return total + (route.durationSeconds || 0);
      }
      return total;
    }, 0);
  },

  totalDuration(state, getters): string {
    return new PrettyTime(getters.totalDurationSeconds).prettyTime();
  },

  formattedRoutesForComponent(state, getters): Array<{id: string, name: string, distance: number, ascent: number, duration: string, route_style?: string, day?: number, isReference?: boolean, plannedTrailId?: number, referencedHikes?: any[]}> {
    return getters.routesWithStats.map((route: any) => ({
      id: route.id,
      name: route.name,
      distance: route.distance,
      ascent: route.ascent,
      duration: route.duration,
      route_style: route.route_style,
      day: route.day,
      isReference: route.isReference,
      plannedTrailId: route.plannedTrailId,
      referencedHikes: route.referencedHikes
    }));
  },

  // Group routes by day
  routesByDay(state, getters): { [day: number]: Array<any> } {
    const routes = getters.formattedRoutesForComponent;
    const routesByDay: { [day: number]: Array<any> } = {};

    routes.forEach(route => {
      if (route.day !== undefined) {
        if (!routesByDay[route.day]) {
          routesByDay[route.day] = [];
        }
        routesByDay[route.day].push(route);
      }
    });

    return routesByDay;
  },

  // Get max day number
  maxDay(state, getters): number {
    const routes = getters.routesArray;
    const days = routes.filter(r => r.day !== undefined).map(r => r.day);
    return days.length > 0 ? Math.max(...days) : 0;
  },

  // Connectors between consecutive stages whose end/start points are close
  // together (see services/stage-connectors.ts for the rules).
  stageConnectors(state): StageConnector[] {
    return detectStageConnectors(state.routes, undefined, state.disconnectedStageIds);
  },

  // Numbered end-of-stage markers for every main-line stage except the last
  // — draggable connector handles when connected, static when floating.
  stageEndMarkers(state): StageEndMarker[] {
    return detectStageEndMarkers(state.routes, undefined, state.disconnectedStageIds);
  },

  // Day-stage boundaries NOT currently connected (too far apart, or
  // explicitly disconnected) — mirror of stageConnectors, drives the
  // "Reconnect to Day N" affordance.
  reconnectableStageLinks(state): ReconnectableStageLink[] {
    return findReconnectableStageLinks(state.routes, undefined, state.disconnectedStageIds);
  },

  // Stage destinations that are ACTUALLY attached — day exists and its end
  // marker sits on the item (stale entries are excluded).
  attachedStageDestinations(state): { [dayNumber: number]: AttachedStageDestination } {
    return resolveAttachedDestinations(state.routes, state.stageDestinations);
  },

  // The trail's start destination, ACTUALLY attached — null unless the
  // trail's first marker still sits on the recorded item.
  attachedTrailStart(state): AttachedTrailStart | null {
    return resolveAttachedTrailStart(state.routes, state.trailStartDestination);
  },

  // Per-day planning stats for the Days list: estimated hiking time across
  // the day's MAIN routes (fast = the user's preferred pace, slow = one
  // speed notch slower), plus sunrise/sunset at the day's start location.
  // Day N is dated plannedToHikeOn + (N - 1) days, falling back to today.
  dayStats(state, getters): {
    [day: number]: {
      durationFastSeconds: number;
      durationSlowSeconds: number;
      sunrise: Date | null;
      sunset: Date | null;
    }
  } {
    const fastPace = HikeTimeUtility.preference();
    const slowPace = fastPace.oneStepSlower();
    const baseDate = state.plannedToHikeOn ? moment(state.plannedToHikeOn) : moment();

    const stats: { [day: number]: any } = {};
    const startCoords: { [day: number]: number[] | null } = {};

    // routesWithStats is position-sorted, so the first main route seen per
    // day is the day's first stage — its start anchors the sun times
    getters.routesWithStats.forEach((route: any) => {
      const day = route.day ?? 1;
      if (!stats[day]) {
        stats[day] = { durationFastSeconds: 0, durationSlowSeconds: 0, sunrise: null, sunset: null };
        startCoords[day] = null;
      }
      if ((route.route_style || 'route') !== 'route') return;
      stats[day].durationFastSeconds += route.durationSeconds || 0;
      stats[day].durationSlowSeconds += slowPace.secondsDuration(route.ascent || 0, route.distance || 0);
      if (!startCoords[day] && route.markers?.length > 0) {
        startCoords[day] = route.markers[0].markerGeometry.coordinates;
      }
    });

    Object.keys(stats).forEach(dayKey => {
      const day = parseInt(dayKey);
      const coords = startCoords[day];
      if (!coords) return;
      // Noon avoids picking the neighbouring solar cycle near midnight
      const date = baseDate.clone().add(day - 1, 'days').hour(12).minute(0).second(0).toDate();
      const times = getSunTimes(date, coords[1], coords[0]);
      if (times) {
        stats[day].sunrise = times.sunrise;
        stats[day].sunset = times.sunset;
      }
    });

    return stats;
  },

  // Read-only day-by-day trip summary (name, description, distance, ascent,
  // duration, sunrise/sunset, attached start/end destinations) for the
  // Itinerary modal — pure composition of getters already computed above,
  // see services/itinerary.ts for the shaping logic.
  itinerary(state, getters): ItineraryData {
    return buildItinerary({
      routes: state.routes,
      routesByDay: getters.routesByDay,
      dayInfo: state.dayInfo || {},
      dayStats: getters.dayStats,
      attachedStageDestinations: getters.attachedStageDestinations,
      attachedTrailStart: getters.attachedTrailStart,
      totalDistanceMeters: getters.totalDistanceExcludingTransport,
      totalAscentMeters: getters.totalAscent,
      totalDurationSeconds: getters.totalDurationSeconds,
    });
  },

  // Check if we should show days
  shouldShowDays(state, getters): boolean {
    const routes = getters.routesArray;
    return routes.length > 1;
  },

  // Whether the current main-line routes form a single continuous walking
  // line that guided mode can lock to (see utils/masterTrail.ts).
  canEnableGuidedMode(state, getters): boolean {
    return canDeriveMasterTrail(getters.routesArray);
  },

  // Get planned trails attributes for hike creation
  getPlannedTrailsAttributesForHike(state, getters): Array<any> {
    const routesArray = getters.routesArray;
    // Filter out incomplete routes (routes with less than 2 markers can't form a valid route)
    const validRoutes = routesArray.filter((route: Route) => route.markers.length >= 2);
    return validRoutes.map((route: Route, index: number) => {
      // Convert route markers and linestrings to attributes
      const markerAttributes = route.markers.map(marker => {
        let markerFeature = plainToClass(MarkerFeature, marker)
        return {
          elevationMeters: Math.round(markerFeature.markerProperties.ele),
          lonlat: `POINT(${markerFeature.markerGeometry.coordinates[0]} ${markerFeature.markerGeometry.coordinates[1]})`,
          snap: markerFeature.markerProperties.isSnap
        }
      });

      const lineStringAttributes = route.lineStrings.map(lineString => {
        let lineStringFeature = plainToClass(LineStringFeature, lineString)
        const coordinates = lineStringFeature.lineStringGeometry.coordinates;

        // Defensive: skip invalid linestrings to avoid backend geometry errors
        if (!coordinates || coordinates.length < 2) {
          console.warn('[SAVE] Skipping linestring with less than 2 coordinates', {
            routeId: route.id,
            lineStringId: lineStringFeature.lineStringProperties.id
          });
          return null;
        }

        // Extract surfaces data
        const surfaceValues = lineStringFeature.lineStringProperties.extras.surface.values;
        const coordinatesCount = coordinates.length;

        // Convert surface segments to array where each element represents surface for each coordinate
        const surfaces: number[] = new Array(coordinatesCount).fill(0); // Default to 0 (unknown)

        // Process each surface segment
        surfaceValues.forEach(segment => {
          const [startIdx, endIdx, surfaceType] = segment;
          // Map surface type string to integer if needed
          let surfaceInt: number;
          if (typeof surfaceType === 'string') {
            // Canonical string→integer surface map (matches
            // LineStringPropertiesSurface.surfaceIntToString and the GraphQL
            // wire contract). `missing` maps to 0 (unknown).
            surfaceInt = surfaceKeyToInt[surfaceType] ?? 0;
          } else {
            surfaceInt = surfaceType as number;
          }

          // Fill the surface array for this segment
          for (let i = startIdx; i < endIdx && i < coordinatesCount; i++) {
            surfaces[i] = surfaceInt;
          }
        });

        const distances: number[] = [];

        if (coordinates.length > 0) {
          distances.push(0);

          for (let i = 1; i < coordinates.length; i++) {
            const previous = coordinates[i - 1];
            const current = coordinates[i];
            const segmentDistance = turfDistance(
              [previous[0], previous[1]],
              [current[0], current[1]],
              { units: 'meters' }
            );

            const cumulative = distances[i - 1] + segmentDistance;
            distances.push(Math.round(cumulative * 10) / 10);
          }
        }

        return {
          geometry: lineStringFeature.toWKT(distances),
          surfaces: surfaces,
          distances: distances
        }
      }).filter((ls): ls is any => !!ls);

      const attributes: any = {
        title: route.name,
        markersAttributes: markerAttributes,
        lineStringsAttributes: lineStringAttributes,
        position: index + 1,
        public: state.public, // Use the same privacy setting as the hiking trail
        routeStyle: route.route_style, // Include route style
        day: route.day // Include day for multi-day hikes
      };

      // If this is a referenced route, include the ptv2PlannedTrailId
      // This will update the existing PlannedTrail and associate it with this hike
      // Note: Updates to the PlannedTrail will affect all hikes that reference it
      if (route.plannedTrailId && route.isReference) {
        attributes.ptv2PlannedTrailId = String(route.plannedTrailId);
      }

      return attributes;
    });
  },

  getHikeDaysAttributes(state, getters): Array<any> {
    // Create a hike_day for each day that has at least one route.
    // Merge in optional name/description from state.dayInfo when present.
    const routesByDay = getters.routesByDay || {};
    const usedDays: number[] = Object.keys(routesByDay)
      .map(d => parseInt(d))
      .filter(d => routesByDay[d] && routesByDay[d].length > 0)
      .sort((a, b) => a - b);

    // The day that currently owns the trail's overall start — only ITS
    // startItem* fields get populated below, mirroring how
    // hydrateStageDestinationsFromHikeDays resolves it back on load.
    const attachedTrailStart = getters.attachedTrailStart;
    const trailStartDayNumber = attachedTrailStart
      ? (state.routes[attachedTrailStart.routeKey]?.day ?? 1)
      : null;
    const attachedStageDestinations = getters.attachedStageDestinations || {};

    const hikeDaysArray: any[] = [];

    usedDays.forEach(dayNumber => {
      const info = state.dayInfo?.[dayNumber] || {};
      const dayAttribute: any = { dayNumber };
      if (info.id) dayAttribute.id = info.id; // include id on updates
      if (info.name) dayAttribute.name = info.name;
      if (info.description) dayAttribute.description = info.description;

      // Always sent explicitly (null when unattached), never omitted —
      // omitting a GraphQL argument leaves the existing column untouched,
      // but detaching is a real user action that must clear it.
      const startDestination = (dayNumber === trailStartDayNumber) ? attachedTrailStart?.destination : null;
      dayAttribute.startItemType = startDestination?.itemType ?? null;
      dayAttribute.startItemId = startDestination?.itemId ?? null;
      dayAttribute.startItemName = startDestination?.name ?? null;
      dayAttribute.startItemPoiType = startDestination?.poiType ?? null;
      // The item's own position, persisted so hydration can tell an intact
      // attachment from one whose marker was moved by a client that doesn't
      // know about attachments (an older mobile build).
      dayAttribute.startItemLon = startDestination?.coordinates?.[0] ?? null;
      dayAttribute.startItemLat = startDestination?.coordinates?.[1] ?? null;

      const endDestination = attachedStageDestinations[dayNumber]?.destination;
      dayAttribute.endItemType = endDestination?.itemType ?? null;
      dayAttribute.endItemId = endDestination?.itemId ?? null;
      dayAttribute.endItemName = endDestination?.name ?? null;
      dayAttribute.endItemPoiType = endDestination?.poiType ?? null;
      dayAttribute.endItemLon = endDestination?.coordinates?.[0] ?? null;
      dayAttribute.endItemLat = endDestination?.coordinates?.[1] ?? null;

      hikeDaysArray.push(dayAttribute);
    });

    return hikeDaysArray;
  },

  // Calculate difficulty based on elevation gain per km
  calculatedDifficulty(state, getters): string {
    const distanceKm = getters.totalDistanceExcludingTransport / 1000;
    if (distanceKm === 0) return 'Easy';

    const elevationGainPerKm = getters.totalAscent / distanceKm;

    if (elevationGainPerKm < 20) return 'Easy';
    if (elevationGainPerKm < 60) return 'Medium';
    if (elevationGainPerKm < 101) return 'Difficult';
    return 'Extra Difficult';
  },

  // Get the final difficulty (override or calculated)
  difficulty(state, getters): string {
    return state.difficultyOverride || getters.calculatedDifficulty;
  },

  // Get bounds of the currently active route, or earth bounds if empty
  activeRouteBounds(state, getters): [[number, number], [number, number]] {
    const activeRoute = getters.activeRoute;

    // Return earth bounds if no active route
    if (!activeRoute) {
      // Earth bounds: [minLng, minLat], [maxLng, maxLat]
      return [[-180, -85], [180, 85]];
    }

    // Check if route has any content (markers or lineStrings)
    if (activeRoute.markers.length === 0 && activeRoute.lineStrings.length === 0) {
      return [[-180, -85], [180, 85]];
    }

    // Combine both markers and lineStrings into a feature collection
    const features = [];

    // Add lineStrings if any
    if (activeRoute.lineStrings.length > 0) {
      const lineStringFeatures = activeRoute.lineStrings.map(lineString => {
        const lineStringFeature: LineStringFeature = plainToClass(LineStringFeature, lineString);
        return lineStringFeature.toGeoJson();
      });
      features.push(...lineStringFeatures);
    }

    // Add markers if any
    if (activeRoute.markers.length > 0) {
      const markerFeatures = activeRoute.markers.map(marker => {
        const markerFeature: MarkerFeature = plainToClass(MarkerFeature, marker);
        return markerFeature.toGeoJson();
      });
      features.push(...markerFeatures);
    }

    // If we have features, calculate bounds
    if (features.length > 0) {
      const featureCollection = {
        type: 'FeatureCollection',
        features: features
      };

      const bounds = turfBbox(featureCollection);

      // Return in Mapbox format: [sw, ne]
      const mapboxBounds: [[number, number], [number, number]] = [[bounds[0], bounds[1]], [bounds[2], bounds[3]]];
      return mapboxBounds;
    }

    // Fallback to earth bounds
    return [[-180, -85], [180, 85]];
  },
  
  // Get hike highlights formatted for GraphQL
  getHikeHighlightsAttributes(state) {
    return state.hikeHighlights.map(highlight => {
      const attributes: any = {
        type: highlight.type,
        title: highlight.title,
        description: highlight.description,
        lonlat: `POINT(${highlight.coordinates[0]} ${highlight.coordinates[1]})`,
        dayNumber: highlight.dayNumber,
        clientId: highlight.clientId,
        notificationDistance: normalizeNotificationDistance(highlight.notificationDistance)
      };
      
      // Add images_attributes if there are signed IDs
      const imagesAttributes: any[] = [];

      if (highlight.images && highlight.images.length > 0) {
        highlight.images.forEach((image: any, index: number) => {
          if (!image) return;
          const clientId = image.clientId || image.client_id || `${highlight.clientId}_existing_${index}`;
          const attr: any = {
            clientId
          };

          if (image.id) {
            attr.id = image.id;
          }

          if (image._destroy) {
            attr._destroy = true;
          }

          imagesAttributes.push(attr);
        });
      }

      if (highlight.imageSignedIds && highlight.imageSignedIds.length > 0) {
        highlight.imageSignedIds
          .forEach((upload: any, index: number) => {
            if (!upload) return;

            if (typeof upload === 'string') {
              imagesAttributes.push({
                signedId: upload,
                clientId: `${highlight.clientId}_image_${index}`
              });
              return;
            }

            const signedId = upload.signedId || upload.signed_id;
            if (!signedId) {
              return;
            }

            const clientId = upload.clientId || upload.client_id || `${highlight.clientId}_image_${index}`;

            imagesAttributes.push({
              signedId,
              clientId
            });
          });
      }

      if (imagesAttributes.length > 0) {
        attributes.imagesAttributes = imagesAttributes;
      }

      // Build media_attachments_attributes (audio etc). mediaType is required on
      // every entry by the backend input. Removal is by omission (replace-state),
      // so existing media flagged _destroy are simply left out of the list.
      const mediaAttachmentsAttributes: any[] = [];

      if (highlight.media && highlight.media.length > 0) {
        highlight.media.forEach((item: any, index: number) => {
          if (!item || item._destroy) return;
          const clientId = item.clientId || item.client_id || `${highlight.clientId}_media_existing_${index}`;
          const attr: any = {
            clientId,
            mediaType: item.mediaType || item.media_type || 'audio'
          };
          if (item.id) attr.id = item.id;
          mediaAttachmentsAttributes.push(attr);
        });
      }

      if (highlight.mediaSignedIds && highlight.mediaSignedIds.length > 0) {
        highlight.mediaSignedIds.forEach((upload: any, index: number) => {
          if (!upload) return;
          const signedId = upload.signedId || upload.signed_id;
          if (!signedId) return;
          mediaAttachmentsAttributes.push({
            signedId,
            clientId: upload.clientId || upload.client_id || `${highlight.clientId}_media_${index}`,
            mediaType: upload.mediaType || upload.media_type || 'audio',
            title: upload.title
          });
        });
      }

      if (mediaAttachmentsAttributes.length > 0) {
        attributes.mediaAttachmentsAttributes = mediaAttachmentsAttributes;
      }

      return attributes;
    });
  }
}
export default getters
